import React, { useState, useEffect } from 'react';
import snippetAlbumService from '../services/snippetAlbumService';
import { SnippetAlbum, SnippetMedia, AlbumWithMedia } from '../types/contentManagement';
import ContentModal from './ContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const SnippetAlbumManagement: React.FC = () => {
  const [albums, setAlbums] = useState<SnippetAlbum[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumWithMedia | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Album modal states
  const [isAlbumModalOpen, setIsAlbumModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<Partial<SnippetAlbum> | null>(null);
  const [albumCoverFile, setAlbumCoverFile] = useState<File | null>(null);
  
  // Media modal states
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [selectedMediaFiles, setSelectedMediaFiles] = useState<File[]>([]);
  const [uploadingToAlbumId, setUploadingToAlbumId] = useState<number | null>(null);
  
  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'album' | 'media', id: number, name: string } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState<'albums' | 'media'>('albums');

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      const data = await snippetAlbumService.getAllAlbums(true);
      const sortedData = (data || []).sort((a, b) => 
        new Date(b.event_date || '').getTime() - new Date(a.event_date || '').getTime()
      );
      setAlbums(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch albums');
      console.error('Error fetching albums:', err);
      setAlbums([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAlbum = () => {
    setEditingAlbum({
      event_name: '',
      description: '',
      event_date: new Date().toISOString().split('T')[0],
      is_visible: true,
      sort_order: albums.length + 1
    });
    setAlbumCoverFile(null);
    setIsAlbumModalOpen(true);
  };

  const handleEditAlbum = (album: SnippetAlbum) => {
    // Convert date to yyyy-mm-dd format for date input
    let formattedDate = '';
    
    console.log('Original album date:', album.event_date);
    
    if (album.event_date) {
      const dateStr = album.event_date;
      
      // Check if it's in dd-mm-yyyy format
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          // Check if first part is day (1-2 digits) - dd-mm-yyyy format
          if (parts[0].length <= 2 && parseInt(parts[0]) <= 31) {
            formattedDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          } 
          // Otherwise assume it's already in yyyy-mm-dd format
          else if (parts[0].length === 4) {
            formattedDate = dateStr;
          }
        }
      }
      // Check if it's in ISO format (yyyy-mm-ddTHH:mm:ss)
      else if (dateStr.includes('T')) {
        formattedDate = dateStr.split('T')[0];
      }
    }
    
    console.log('Formatted date for input:', formattedDate);
    
    setEditingAlbum({ 
      ...album,
      event_date: formattedDate
    });
    setAlbumCoverFile(null);
    setIsAlbumModalOpen(true);
  };

  const handleSaveAlbum = async () => {
    if (!editingAlbum || !editingAlbum.event_name) {
      setSaveMessage('Error: Event name is required');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      formData.append('event_name', editingAlbum.event_name);
      if (editingAlbum.description) formData.append('description', editingAlbum.description);
      if (editingAlbum.event_date) formData.append('event_date', editingAlbum.event_date);
      formData.append('is_visible', String(editingAlbum.is_visible ?? true));
      formData.append('sort_order', String(editingAlbum.sort_order || albums.length + 1));
      
      if (albumCoverFile) {
        formData.append('cover_image', albumCoverFile);
      }

      if (editingAlbum.id) {
        await snippetAlbumService.updateAlbum(editingAlbum.id, formData);
        setSaveMessage('Album updated successfully');
      } else {
        await snippetAlbumService.createAlbum(formData);
        setSaveMessage('Album created successfully');
      }
      
      await fetchAlbums();
      setIsAlbumModalOpen(false);
      setEditingAlbum(null);
      setAlbumCoverFile(null);
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Error saving album';
      setSaveMessage(`Error: ${errorMessage}`);
      console.error('Error saving album:', err);
      console.error('Error details:', err?.response?.data);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMediaToAlbum = (albumId: number | null) => {
    setUploadingToAlbumId(albumId);
    setSelectedMediaFiles([]);
    setIsMediaModalOpen(true);
  };

  const handleMediaFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedMediaFiles(Array.from(e.target.files));
    }
  };

  const handleUploadMedia = async () => {
    if (selectedMediaFiles.length === 0) {
      setSaveMessage('Error: Please select at least one file');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      if (uploadingToAlbumId !== null) {
        formData.append('album_id', String(uploadingToAlbumId));
      }
      
      selectedMediaFiles.forEach((file, index) => {
        formData.append('media', file);
      });

      await snippetAlbumService.uploadMedia(formData);
      setSaveMessage(`${selectedMediaFiles.length} file(s) uploaded successfully`);
      
      await fetchAlbums();
      setIsMediaModalOpen(false);
      setSelectedMediaFiles([]);
      setUploadingToAlbumId(null);
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error uploading media');
      console.error('Error uploading media:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewAlbumMedia = async (albumId: number) => {
    try {
      const albumWithMedia = await snippetAlbumService.getAlbumWithMedia(albumId);
      setSelectedAlbum(albumWithMedia);
    } catch (err) {
      console.error('Error fetching album media:', err);
    }
  };

  const handleViewOthersMedia = async () => {
    try {
      const othersMedia = await snippetAlbumService.getMediaWithoutAlbum(true);
      setSelectedAlbum({
        id: 0,
        event_name: 'Others',
        description: 'Uncategorized photos and videos',
        is_visible: true,
        sort_order: 0,
        created_at: new Date(),
        updated_at: new Date(),
        media: othersMedia
      } as any);
    } catch (err) {
      console.error('Error fetching Others media:', err);
    }
  };

  const handleDeleteClick = (type: 'album' | 'media', id: number, name: string) => {
    setDeleteTarget({ type, id, name });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setSubmitting(true);
      
      if (deleteTarget.type === 'album') {
        await snippetAlbumService.deleteAlbum(deleteTarget.id);
        setSaveMessage('Album deleted successfully');
      } else {
        await snippetAlbumService.deleteMedia(deleteTarget.id);
        setSaveMessage('Media deleted successfully');
      }
      
      await fetchAlbums();
      if (selectedAlbum && deleteTarget.type === 'media') {
        await handleViewAlbumMedia(selectedAlbum.id);
      }
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage(`Error deleting ${deleteTarget.type}`);
      console.error(`Error deleting ${deleteTarget.type}:`, err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleAlbumVisibility = async (id: number) => {
    try {
      await snippetAlbumService.toggleAlbumVisibility(id);
      await fetchAlbums();
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading albums...</div>;
  }

  return (
    <div className="edit-section">
      {saveMessage && !isAlbumModalOpen && !isMediaModalOpen && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: saveMessage.includes('Error') ? '#fee' : '#efe',
          color: saveMessage.includes('Error') ? '#c33' : '#3c3',
          borderRadius: '4px',
          border: `1px solid ${saveMessage.includes('Error') ? '#fcc' : '#cfc'}`
        }}>
          {saveMessage}
        </div>
      )}

      <div className="applications-list">
        <div className="list-header">
          <h4>Gallery Albums (Event-based Photo/Video Gallery)</h4>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className="btn-add" 
              onClick={handleAddAlbum}
              disabled={submitting}
            >
              <i className="icon-plus"></i>
              Add Album
            </button>
            <button 
              className="btn-add" 
              onClick={() => handleAddMediaToAlbum(null)}
              disabled={submitting}
              style={{ backgroundColor: '#6c757d' }}
            >
              <i className="icon-plus"></i>
              Add to Others
            </button>
            <button 
              className="btn-add" 
              onClick={handleViewOthersMedia}
              disabled={submitting}
              style={{ backgroundColor: '#17a2b8' }}
            >
              <i className="icon-eye"></i>
              View Others
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#c33' }}>
            {error}
          </div>
        )}

        <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '50px' }}>Cover</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Event Name</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '120px' }}>Event Date</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '80px' }}>Media</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '80px' }}>Visible</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {albums.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                    No albums found. Click "Add Album" to create one.
                  </td>
                </tr>
              ) : (
                albums.map((album) => (
                  <tr key={album.id} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {album.cover_image ? (
                        <img 
                          src={snippetAlbumService.getFileUrl(album.cover_image)} 
                          alt={album.event_name}
                          style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ) : (
                        <div style={{ width: '40px', height: '40px', backgroundColor: '#e9ecef', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          📁
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <strong>{album.event_name}</strong>
                      {album.description && (
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                          {album.description.substring(0, 60)}{album.description.length > 60 ? '...' : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {album.event_date || 'N/A'}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <span style={{ 
                        backgroundColor: '#e3f2fd', 
                        color: '#1976d2', 
                        padding: '4px 8px', 
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {album.media_count || 0}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={album.is_visible}
                        onChange={() => handleToggleAlbumVisibility(album.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          onClick={() => handleViewAlbumMedia(album.id)}
                          disabled={submitting}
                          title="View media"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '16px'
                          }}
                        >
                          👁️
                        </button>
                        <button 
                          onClick={() => handleAddMediaToAlbum(album.id)}
                          disabled={submitting}
                          title="Add media"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '16px'
                          }}
                        >
                          ➕
                        </button>
                        <button 
                          onClick={() => handleEditAlbum(album)}
                          disabled={submitting}
                          title="Edit album"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '16px'
                          }}
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={() => handleDeleteClick('album', album.id, album.event_name)}
                          disabled={submitting}
                          title="Delete album"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '16px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Album Modal */}
      <ContentModal
        isOpen={isAlbumModalOpen}
        onClose={() => {
          setIsAlbumModalOpen(false);
          setEditingAlbum(null);
          setAlbumCoverFile(null);
        }}
        title={editingAlbum?.id ? 'Edit Album' : 'Add Album'}
      >
        {saveMessage && isAlbumModalOpen && (
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: saveMessage.includes('Error') ? '#fee' : '#efe',
            color: saveMessage.includes('Error') ? '#c33' : '#3c3',
            borderRadius: '8px',
            border: `2px solid ${saveMessage.includes('Error') ? '#fcc' : '#cfc'}`,
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            fontWeight: '600'
          }}>
            {saveMessage}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Event Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={editingAlbum?.event_name || ''}
              onChange={(e) => setEditingAlbum((prev: any) => ({ ...prev, event_name: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="e.g., Diwali Celebration 2024"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Description</label>
            <textarea
              value={editingAlbum?.description || ''}
              onChange={(e) => setEditingAlbum((prev: any) => ({ ...prev, description: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '80px' }}
              placeholder="Brief description of the event..."
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Event Date</label>
            <input
              type="date"
              value={editingAlbum?.event_date || ''}
              onChange={(e) => setEditingAlbum((prev: any) => ({ ...prev, event_date: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Cover Image</label>
            {editingAlbum?.cover_image && !albumCoverFile && (
              <div style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '500' }}>
                  Current: {editingAlbum.cover_image.split('/').pop()}
                </p>
                <img 
                  src={`http://localhost:5001${editingAlbum.cover_image}`} 
                  alt="Current cover" 
                  style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setAlbumCoverFile(e.target.files?.[0] || null)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
              {albumCoverFile ? `New file selected: ${albumCoverFile.name}` : 'Choose a new file to replace the current image'}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="album_visible"
              checked={editingAlbum?.is_visible ?? true}
              onChange={(e) => setEditingAlbum((prev: any) => ({ ...prev, is_visible: e.target.checked }))}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="album_visible" style={{ cursor: 'pointer', fontWeight: '500' }}>
              Visible on homepage
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
            <button
              onClick={() => {
                setIsAlbumModalOpen(false);
                setEditingAlbum(null);
                setAlbumCoverFile(null);
              }}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAlbum}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#00a0e3',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Save Album'}
            </button>
          </div>
        </div>
      </ContentModal>

      {/* Media Upload Modal */}
      <ContentModal
        isOpen={isMediaModalOpen}
        onClose={() => {
          setIsMediaModalOpen(false);
          setSelectedMediaFiles([]);
          setUploadingToAlbumId(null);
        }}
        title={`Upload Media ${uploadingToAlbumId === null ? 'to Others' : 'to Album'}`}
      >
        {saveMessage && isMediaModalOpen && (
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: saveMessage.includes('Error') ? '#fee' : '#efe',
            color: saveMessage.includes('Error') ? '#c33' : '#3c3',
            borderRadius: '8px',
            border: `2px solid ${saveMessage.includes('Error') ? '#fcc' : '#cfc'}`,
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            fontWeight: '600'
          }}>
            {saveMessage}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Select Photos/Videos <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleMediaFilesChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              You can select multiple files at once
            </p>
            {selectedMediaFiles.length > 0 && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <strong>Selected files ({selectedMediaFiles.length}):</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '12px' }}>
                  {selectedMediaFiles.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
            <button
              onClick={() => {
                setIsMediaModalOpen(false);
                setSelectedMediaFiles([]);
                setUploadingToAlbumId(null);
              }}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUploadMedia}
              disabled={submitting || selectedMediaFiles.length === 0}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#00a0e3',
                color: 'white',
                cursor: (submitting || selectedMediaFiles.length === 0) ? 'not-allowed' : 'pointer',
                opacity: (submitting || selectedMediaFiles.length === 0) ? 0.6 : 1
              }}
            >
              {submitting ? 'Uploading...' : `Upload ${selectedMediaFiles.length} file(s)`}
            </button>
          </div>
        </div>
      </ContentModal>

      {/* Album Media View Modal */}
      {selectedAlbum && (
        <ContentModal
          isOpen={!!selectedAlbum}
          onClose={() => setSelectedAlbum(null)}
          title={`${selectedAlbum.event_name} - Media (${selectedAlbum.media?.length || 0})`}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
            {selectedAlbum.media && selectedAlbum.media.length > 0 ? (
              selectedAlbum.media.map((media) => (
                <div key={media.id} style={{ position: 'relative', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                  {media.media_type === 'image' ? (
                    <img 
                      src={snippetAlbumService.getFileUrl(media.file_path)} 
                      alt={media.caption || ''}
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    />
                  ) : (
                    <video 
                      src={snippetAlbumService.getFileUrl(media.file_path)}
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                      controls
                    />
                  )}
                  <button
                    onClick={() => handleDeleteClick('media', media.id, media.caption || 'media')}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              ))
            ) : (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#999', padding: '20px' }}>
                No media in this album yet
              </p>
            )}
          </div>
        </ContentModal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${deleteTarget?.type === 'album' ? 'Album' : 'Media'}`}
        message={`Are you sure you want to delete this ${deleteTarget?.type}?${deleteTarget?.type === 'album' ? ' All media in this album will also be deleted.' : ''}`}
        itemName={deleteTarget?.name}
      />
    </div>
  );
};

export default SnippetAlbumManagement;
